import { memo, useState } from 'react'
import {
  Box,
  Text,
  HStack,
  VStack,
  useColorModeValue,
  Tooltip,
  ButtonGroup,
  Button,
  Badge,
  SimpleGrid,
  Spinner,
} from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { colors, components } from '../../theme'
import { STRINGS } from '../../resources'
import type { Severity, AnalysisBreakdown as AnalysisBreakdownType } from '../../types/vulnerability'

interface AnalysisBreakdownProps {
  breakdown: AnalysisBreakdownType | null  // Pre-computed by worker
  isFiltering?: boolean
}

interface SeverityBreakdown {
  critical: number
  high: number
  medium: number
  low: number
}

interface CategoryData {
  key: 'valid' | 'ai' | 'manual'
  label: string
  tooltip: string
  count: number
  percentage: number
  color: string
  colorLight: string
  severities: SeverityBreakdown
}

type ViewType = 'cards' | 'compare'

export const AnalysisBreakdown = memo(function AnalysisBreakdown({
  breakdown,
  isFiltering = false,
}: AnalysisBreakdownProps) {
  const [viewType, setViewType] = useState<ViewType>('cards')

  const cardBg = useColorModeValue(colors.ui.card.light, colors.ui.card.dark)
  const borderColor = useColorModeValue(colors.ui.border.light, colors.ui.border.dark)
  const labelColor = useColorModeValue(colors.ui.text.secondary.light, colors.ui.text.secondary.dark)
  const textColor = useColorModeValue(colors.ui.text.primary.light, colors.ui.text.primary.dark)
  const overlayBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.600')
  // Colors for nested render functions (must be at top level due to Rules of Hooks)
  const totalRowBg = useColorModeValue('gray.50', 'gray.700')
  const aiBoxBg = useColorModeValue('purple.50', 'purple.900')
  const ratioBoxBg = useColorModeValue('gray.100', 'gray.700')
  const manualBoxBg = useColorModeValue('gray.50', 'gray.800')
  const insightBg = useColorModeValue('blue.50', 'blue.900')

  // Early return if no data
  if (!breakdown) return null

  // Use pre-computed breakdown from worker - no main thread loops!
  const categories: CategoryData[] = [
    {
      key: 'valid',
      label: STRINGS.ANALYSIS_STATUS.VALID,
      tooltip: STRINGS.TOOLTIPS.ANALYSIS_VALID,
      count: breakdown.valid.count,
      percentage: breakdown.total > 0 ? (breakdown.valid.count / breakdown.total) * 100 : 0,
      color: '#D97706',  // Amber - "needs attention"
      colorLight: '#FFFBEB',
      severities: breakdown.valid.bySeverity,
    },
    {
      key: 'ai',
      label: STRINGS.ANALYSIS_STATUS.AI_NO_RISK,
      tooltip: STRINGS.TOOLTIPS.ANALYSIS_AI,
      count: breakdown.ai.count,
      percentage: breakdown.total > 0 ? (breakdown.ai.count / breakdown.total) * 100 : 0,
      color: colors.filter.ai.active,
      colorLight: '#FAF5FF',
      severities: breakdown.ai.bySeverity,
    },
    {
      key: 'manual',
      label: STRINGS.ANALYSIS_STATUS.MANUAL_NO_RISK,
      tooltip: STRINGS.TOOLTIPS.ANALYSIS_MANUAL,
      count: breakdown.manual.count,
      percentage: breakdown.total > 0 ? (breakdown.manual.count / breakdown.total) * 100 : 0,
      color: colors.filter.manual.active,
      colorLight: '#F7FAFC',
      severities: breakdown.manual.bySeverity,
    },
  ]


  const renderSeverityCounts = (severities: SeverityBreakdown) => {
    const severityOrder: Severity[] = ['critical', 'high', 'medium', 'low']
    const colorSchemes: Record<Severity, string> = colors.severityColorScheme as Record<Severity, string>

    return (
      <HStack spacing={2} justify="flex-start">
        {severityOrder.map((sev) => {
          const count = severities[sev]
          if (count === 0) return null
          return (
            <Tooltip key={sev} label={`${sev.charAt(0).toUpperCase() + sev.slice(1)}: ${count.toLocaleString()}`} hasArrow>
              <Badge colorScheme={colorSchemes[sev]} variant="solid" fontSize="xs">
                {sev.charAt(0).toUpperCase()}: {count.toLocaleString()}
              </Badge>
            </Tooltip>
          )
        })}
      </HStack>
    )
  }

  // Cards View - Table layout for consistency
  const renderCardsView = () => (
    <VStack spacing={3} align="stretch">
      <Box>
        <Box as="table" w="100%" fontSize="sm">
          <Box as="thead">
            <Box as="tr" borderBottomWidth="1px" borderColor={borderColor}>
              <Box as="th" textAlign="left" py={2} px={3} color={labelColor} fontWeight="medium">Status</Box>
              <Box as="th" textAlign="right" py={2} px={3} color={labelColor} fontWeight="medium">Count</Box>
              <Box as="th" textAlign="right" py={2} px={3} color={labelColor} fontWeight="medium">%</Box>
              <Box as="th" textAlign="left" py={2} px={3} color={labelColor} fontWeight="medium">Severity</Box>
            </Box>
          </Box>
          <Box as="tbody">
            {categories.map((cat) => (
              <Box as="tr" key={cat.key} borderBottomWidth="1px" borderColor={borderColor} _last={{ borderBottomWidth: 0 }}>
                <Box as="td" py={3} px={3}>
                  <HStack spacing={2}>
                    <Box w={1} h={6} bg={cat.color} borderRadius="full" />
                    <Tooltip label={cat.tooltip} hasArrow>
                      <Text cursor="help">
                        {cat.label}
                      </Text>
                    </Tooltip>
                  </HStack>
                </Box>
                <Box as="td" py={3} px={3} textAlign="right">
                  <Text fontWeight="bold" color={cat.color}>{cat.count.toLocaleString()}</Text>
                </Box>
                <Box as="td" py={3} px={3} textAlign="right" color={labelColor}>
                  {cat.percentage.toFixed(1)}%
                </Box>
                <Box as="td" py={3} px={3}>
                  {renderSeverityCounts(cat.severities)}
                </Box>
              </Box>
            ))}
            {/* Total row */}
            <Box as="tr" bg={totalRowBg}>
              <Box as="td" py={3} px={3} fontWeight="medium">Total</Box>
              <Box as="td" py={3} px={3} textAlign="right" fontWeight="bold">{breakdown.total.toLocaleString()}</Box>
              <Box as="td" py={3} px={3} textAlign="right" color={labelColor}>100%</Box>
              <Box as="td" py={3} px={3}></Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </VStack>
  )


  // Compare View - AI vs Manual efficiency and severity comparison
  const renderCompareView = () => {
    const aiData = categories.find((c) => c.key === 'ai')
    const manualData = categories.find((c) => c.key === 'manual')

    // Safety check - should never happen but prevents crash
    if (!aiData || !manualData) return null
    const totalCleared = aiData.count + manualData.count
    const aiRatio = manualData.count > 0 ? aiData.count / manualData.count : 0

    // Severity comparison data for grouped bar chart
    const severityCompareData = (['critical', 'high', 'medium', 'low'] as Severity[]).map((sev) => ({
      severity: sev.charAt(0).toUpperCase() + sev.slice(1),
      ai: aiData.severities[sev],
      manual: manualData.severities[sev],
      aiPct: aiData.count > 0 ? (aiData.severities[sev] / aiData.count) * 100 : 0,
      manualPct: manualData.count > 0 ? (manualData.severities[sev] / manualData.count) * 100 : 0,
    }))

    return (
      <VStack spacing={4} align="stretch">
        {/* Efficiency Metrics */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          {/* AI Cleared */}
          <Box p={4} borderRadius="md" bg={aiBoxBg} textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color={colors.filter.ai.active}>
              {aiData.count.toLocaleString()}
            </Text>
            <Text fontSize="sm" color={labelColor}>AI Cleared (1st pass)</Text>
            <Text fontSize="xs" color={labelColor}>{aiData.percentage.toFixed(1)}% of total</Text>
          </Box>

          {/* Ratio */}
          <Box p={4} borderRadius="md" bg={ratioBoxBg} textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color={textColor}>
              {aiRatio > 0 ? `${aiRatio.toFixed(1)}x` : '—'}
            </Text>
            <Text fontSize="sm" color={labelColor}>Automation Efficiency</Text>
            <Text fontSize="xs" color={labelColor}>
              {totalCleared.toLocaleString()} total cleared
            </Text>
          </Box>

          {/* Manual Cleared */}
          <Box p={4} borderRadius="md" bg={manualBoxBg} textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color={colors.filter.manual.active}>
              {manualData.count.toLocaleString()}
            </Text>
            <Text fontSize="sm" color={labelColor}>Human Review (2nd pass)</Text>
            <Text fontSize="xs" color={labelColor}>{manualData.percentage.toFixed(1)}% of total</Text>
          </Box>
        </SimpleGrid>

        {/* Severity Distribution Comparison */}
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2} color={labelColor}>
            Severity Distribution: What each clears
          </Text>
          <Box h="200px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={severityCompareData} margin={{ left: 0, right: 20 }}>
                <XAxis dataKey="severity" tick={{ fontSize: 11, fill: labelColor }} />
                <YAxis tick={{ fontSize: 11, fill: labelColor }} />
                <RechartsTooltip
                  formatter={(value: number, name: string) => [
                    value.toLocaleString(),
                    name
                  ]}
                  contentStyle={{ backgroundColor: cardBg, borderColor: borderColor }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="ai" fill={colors.filter.ai.active} name="AI Cleared" />
                <Bar dataKey="manual" fill={colors.filter.manual.active} name="Manually Cleared" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Insight callout */}
        <Box p={3} borderRadius="md" bg={insightBg} borderLeft="4px solid" borderLeftColor="blue.400">
          <Text fontSize="sm" color={textColor} mb={1}>
            {totalCleared === 0
              ? `No AI or manually cleared CVEs in the current filter. Showing only unreviewed vulnerabilities.`
              : aiRatio >= 2
              ? `AI (first pass) is confidently clearing ${aiRatio.toFixed(1)}x more CVEs than require human review, significantly reducing analyst workload.`
              : aiRatio >= 1
              ? `AI and human review are clearing similar volumes. Consider tuning AI rules to confidently clear more patterns automatically.`
              : `Human reviewers are clearing more than AI's first pass. Expand AI rule coverage to reduce manual triage burden.`
            }
          </Text>
          <Text fontSize="xs" color={labelColor}>
            Workflow: AI triages first → Humans review what AI couldn't confidently clear
          </Text>
        </Box>
      </VStack>
    )
  }


  return (
    <Box bg={cardBg} p={4} borderRadius="md" shadow="sm" className="card-glow" position="relative">
      {isFiltering && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={overlayBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
          borderRadius="md"
        >
          <Spinner size="md" color="blue.500" thickness="3px" />
        </Box>
      )}
      {/* Header with toggle buttons */}
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <Text fontSize={components.chart.title.fontSize} fontWeight={components.chart.title.fontWeight}>
          {STRINGS.ANALYSIS_BREAKDOWN.TITLE}
        </Text>
        <ButtonGroup size="xs" isAttached variant="outline">
          <Tooltip label={STRINGS.ANALYSIS_BREAKDOWN.CARDS_TOOLTIP} hasArrow>
            <Button
              onClick={() => setViewType('cards')}
              variant={viewType === 'cards' ? 'solid' : 'outline'}
              colorScheme={viewType === 'cards' ? 'blue' : 'gray'}
            >
              Cards
            </Button>
          </Tooltip>
          <Tooltip label={STRINGS.ANALYSIS_BREAKDOWN.COMPARE_TOOLTIP} hasArrow>
            <Button
              onClick={() => setViewType('compare')}
              variant={viewType === 'compare' ? 'solid' : 'outline'}
              colorScheme={viewType === 'compare' ? 'blue' : 'gray'}
            >
              Compare
            </Button>
          </Tooltip>
        </ButtonGroup>
      </HStack>

      {/* View content */}
      {viewType === 'cards' && renderCardsView()}
      {viewType === 'compare' && renderCompareView()}
    </Box>
  )
})
